<template>
  <div class="ft-page">
    <!-- 顶部 -->
    <div class="ft-top">
      <div class="ft-top-l">
        <div class="ft-title">
          <el-button icon="ArrowLeft" text @click="back">返回</el-button>
          <h2>主题库</h2>
          <span class="ft-badge">{{ themes.length }} 套</span>
        </div>
        <p class="ft-sub">
          把外观沉淀成可复用的主题：配色 / 底色 / 标题色 / 圆角 / 按钮形态。
          套用只是写入该表单的外观配置（快照），之后再改主题<b>不会回溯改老表单</b>。
        </p>
      </div>
      <div class="ft-top-r">
        <el-button type="primary" icon="Plus" @click="openEdit(null)">新建主题</el-button>
      </div>
    </div>

    <!-- 风格筛选 -->
    <div class="ft-bar">
      <span class="ft-bar-label">风格</span>
      <div class="ft-chips">
        <span
            class="ft-chip"
            :class="{ 'is-active': !filterStyle }"
            @click="pickStyle('')">全部</span>
        <span
            v-for="s in styles"
            :key="s"
            class="ft-chip"
            :class="{ 'is-active': filterStyle === s }"
            @click="pickStyle(s)">{{ s }}</span>
      </div>
    </div>

    <!-- 主题网格 -->
    <div v-loading="loading" class="ft-grid">
      <div v-for="t in themes" :key="t.id" class="ft-card">
        <!-- 实时预览：完全按该主题的参数渲染一个迷你表单 -->
        <div class="ft-preview" :style="{ background: bgOf(t) }">
          <div class="ft-pv-card" :style="{ borderRadius: radiusOf(t) + 'px' }">
            <div class="ft-pv-title" :style="{ color: t.titleColor || '#1f2937' }">活动报名表</div>
            <div class="ft-pv-input" :style="{ borderRadius: innerRadius(t) + 'px' }"></div>
            <div class="ft-pv-input ft-pv-input--short" :style="{ borderRadius: innerRadius(t) + 'px' }"></div>
            <div
                class="ft-pv-btn"
                :style="{ background: t.themeColor || '#8dc63f', borderRadius: btnRadius(t) }">提 交</div>
          </div>
        </div>

        <div class="ft-card-body">
          <div class="ft-card-name">
            {{ t.name }}
            <el-tag v-if="t.isPreset === 1" size="small" effect="plain" class="ft-preset">内置</el-tag>
          </div>
          <div class="ft-card-meta">
            <span v-if="t.style" class="ft-style">{{ t.style }}</span>
            <span class="ft-swatch" :style="{ background: t.themeColor || '#8dc63f' }"></span>
            <code class="ft-hex">{{ t.themeColor || '默认' }}</code>
          </div>
        </div>

        <div class="ft-card-foot">
          <el-button size="small" type="primary" text @click="openApply(t)">
            <el-icon><Promotion /></el-icon>应用到表单
          </el-button>
          <el-button size="small" text @click="openEdit(t)">
            <el-icon><EditPen /></el-icon>编辑
          </el-button>
          <el-button
              size="small"
              text
              class="ft-del"
              :disabled="t.isPreset === 1"
              @click="remove(t)">
            <el-icon><Delete /></el-icon>删除
          </el-button>
        </div>
      </div>

      <div v-if="!loading && !themes.length" class="ft-empty">
        <el-empty :description="filterStyle ? ('「' + filterStyle + '」风格下还没有主题') : '还没有任何主题，先建一套自己的外观吧'">
          <el-button type="primary" icon="Plus" @click="openEdit(null)">新建主题</el-button>
          <el-button v-if="filterStyle" @click="pickStyle('')">查看全部风格</el-button>
        </el-empty>
      </div>
    </div>

    <!-- ============ 编辑主题（左配置 / 右实时预览） ============ -->
    <el-dialog
        v-model="editVisible"
        :title="form.id ? '编辑主题' : '新建主题'"
        width="820px"
        align-center
        top="6vh">
      <div class="ft-editor">
        <div class="ft-editor-l">
          <el-form label-width="88px" label-position="left">
            <el-form-item label="主题名">
              <el-input v-model="form.name" maxlength="50" placeholder="例如：暑期活力橙"/>
            </el-form-item>
            <el-form-item label="风格">
              <el-select v-model="form.style" allow-create filterable clearable placeholder="清新 / 活力 / 商务…" class="ft-full">
                <el-option v-for="s in stylePool" :key="s" :label="s" :value="s"/>
              </el-select>
            </el-form-item>
            <el-form-item label="主题色">
              <el-color-picker v-model="form.themeColor" :predefine="presetColors"/>
              <span class="ft-tip">按钮与强调色</span>
            </el-form-item>
            <el-form-item label="页面底色">
              <el-color-picker v-model="form.pageBg" :predefine="presetColors"/>
              <el-button size="small" text @click="form.pageBg = ''">用默认</el-button>
            </el-form-item>
            <el-form-item label="标题颜色">
              <el-color-picker v-model="form.titleColor" :predefine="presetColors"/>
            </el-form-item>
            <el-form-item label="卡片圆角">
              <el-slider v-model="form.cardRadius" :min="0" :max="32" show-stops :step="2" class="ft-slider"/>
              <span class="ft-tip">{{ form.cardRadius }}px</span>
            </el-form-item>
            <el-form-item label="按钮形态">
              <el-radio-group v-model="form.btnRadius">
                <el-radio-button label="round">胶囊</el-radio-button>
                <el-radio-button label="square">直角</el-radio-button>
              </el-radio-group>
            </el-form-item>
            <el-form-item label="排序">
              <el-input-number v-model="form.sort" :min="0" :max="9999"/>
              <span class="ft-tip">越大越靠前</span>
            </el-form-item>
          </el-form>
        </div>

        <div class="ft-editor-r">
          <div class="ft-editor-rlabel">
            <el-icon><View /></el-icon> 实时预览
          </div>
          <div class="ft-editor-phone" :style="{ background: bgOf(form) }">
            <div class="ft-pv-card" :style="{ borderRadius: radiusOf(form) + 'px' }">
              <div class="ft-pv-title" :style="{ color: form.titleColor || '#1f2937' }">活动报名表</div>
              <div class="ft-pv-input" :style="{ borderRadius: innerRadius(form) + 'px' }"></div>
              <div class="ft-pv-input ft-pv-input--short" :style="{ borderRadius: innerRadius(form) + 'px' }"></div>
              <div class="ft-pv-input" :style="{ borderRadius: innerRadius(form) + 'px' }"></div>
              <div
                  class="ft-pv-btn"
                  :style="{ background: form.themeColor || '#8dc63f', borderRadius: btnRadius(form) }">提 交</div>
            </div>
          </div>
          <div class="ft-editor-hint">
            小程序端这四项走 CSS 变量下发：<br/>
            <code>--xf-theme / --xf-page-bg / --xf-radius / --xf-btn-radius</code>
          </div>
        </div>
      </div>

      <template #footer>
        <el-button @click="editVisible = false">取 消</el-button>
        <el-button type="primary" :loading="saving" :disabled="saving" @click="save">保 存 主 题</el-button>
      </template>
    </el-dialog>

    <!-- ============ 应用到表单 ============ -->
    <el-dialog v-model="applyVisible" title="应用到表单" width="480px" align-center>
      <div class="ft-apply-tip">
        主题「<b>{{ applyTheme.name }}</b>」的配色会写入所选表单的外观配置，
        <br/>该表单已有的其它设置（规则 / 推送）不受影响。
      </div>
      <el-select
          v-model="applyFormId"
          filterable
          clearable
          placeholder="搜索并选择要套用的表单"
          class="ft-full">
        <el-option
            v-for="f in formOptions"
            :key="f.id"
            :label="f.title"
            :value="f.id">
          <span class="ft-opt-title">{{ f.title }}</span>
          <span class="ft-opt-code">{{ f.formKey }}</span>
        </el-option>
      </el-select>
      <template #footer>
        <el-button @click="applyVisible = false">取 消</el-button>
        <el-button type="primary" :disabled="!applyFormId" @click="doApply">确 定 套 用</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import {
  formThemeList,
  formThemeSave,
  formThemeDelete,
  formThemeApply,
  formPage,
} from '@/api/form';

const DEFAULT_FORM = () => ({
  id: null,
  name: '',
  style: '',
  themeColor: '#8dc63f',
  pageBg: '',
  titleColor: '#1f2937',
  cardRadius: 12,
  btnRadius: 'round',
  sort: 0,
});

export default {
  name: 'FormTheme',
  data() {
    return {
      loading: false,
      saving: false,
      themes: [],
      styles: [],
      filterStyle: '',
      stylePool: ['品牌', '活力', '商务', '节日', '简约', '清新'],
      presetColors: [
        '#8dc63f', '#67c23a', '#2f6bd8', '#409eff', '#ff7a45',
        '#e54d42', '#f56c6c', '#e6a23c', '#13c2c2', '#6b7280',
      ],
      editVisible: false,
      form: DEFAULT_FORM(),
      // 应用主题
      applyVisible: false,
      applyTheme: {},
      applyFormId: null,
      formOptions: [],
    };
  },
  methods: {
    /* ---------- 展示口径（与小程序端 _themeStyle 保持同一套默认值） ---------- */
    bgOf(t) {
      return (t && t.pageBg) || '#f5f7fa';
    },
    radiusOf(t) {
      const n = Number(t && t.cardRadius);
      return Number.isFinite(n) && n >= 0 ? n : 12;
    },
    /** 内层输入框圆角 = 卡片圆角的一半（视觉层级一致，不会出现"外圆内方"的割裂感） */
    innerRadius(t) {
      return Math.max(0, Math.round(this.radiusOf(t) * 0.6));
    },
    btnRadius(t) {
      return (t && t.btnRadius) === 'square' ? '6px' : '999px';
    },
    /* ---------- 数据 ---------- */
    load() {
      this.loading = true;
      formThemeList(this.filterStyle).then(val => {
        this.loading = false;
        if (val && val.code === 200 && val.data) {
          this.themes = val.data.list || [];
          // styles 是「全量风格」，不随筛选变化，避免筛完某个风格后其它风格标签消失
          if (Array.isArray(val.data.styles)) this.styles = val.data.styles;
        } else {
          this.$message.warning((val && val.message) || '获取主题失败');
        }
      }).catch(() => {
        this.loading = false;
      });
    },
    pickStyle(s) {
      this.filterStyle = s;
      this.load();
    },
    loadForms() {
      formPage({ pageIndex: 1, pageSize: 500 }).then(val => {
        if (val && val.code === 200) {
          this.formOptions = (val.data && val.data.records) || [];
        }
      });
    },
    /* ---------- 编辑 ---------- */
    openEdit(t) {
      this.form = t
          ? {
            id: t.id,
            name: t.name,
            style: t.style || '',
            themeColor: t.themeColor || '#8dc63f',
            pageBg: t.pageBg || '',
            titleColor: t.titleColor || '#1f2937',
            cardRadius: this.radiusOf(t),
            btnRadius: t.btnRadius || 'round',
            sort: t.sort || 0,
          }
          : DEFAULT_FORM();
      this.editVisible = true;
    },
    save() {
      if (!this.form.name || !String(this.form.name).trim()) {
        this.$message.warning('请填写主题名');
        return;
      }
      this.saving = true;
      formThemeSave({
        id: this.form.id,
        name: String(this.form.name).trim(),
        style: this.form.style || null,
        themeColor: this.form.themeColor || null,
        pageBg: this.form.pageBg || null,
        titleColor: this.form.titleColor || null,
        cardRadius: Number(this.form.cardRadius) || 0,
        btnRadius: this.form.btnRadius || 'round',
        sort: Number(this.form.sort) || 0,
      }).then(val => {
        this.saving = false;
        if (val && val.code === 200) {
          this.$message.success('保存成功');
          this.editVisible = false;
          this.load();
        } else {
          this.$message.warning((val && val.message) || '保存失败');
        }
      }).catch(() => {
        this.saving = false;
        this.$message.error('保存主题失败');
      });
    },
    remove(t) {
      this.$confirm(`确定删除主题「${t.name}」吗？已套用过该主题的表单外观不受影响。`, '提示', { type: 'warning' })
          .then(() => {
            formThemeDelete(t.id).then(val => {
              if (val && val.code === 200) {
                this.$message.success('已删除');
                this.load();
              } else {
                this.$message.warning((val && val.message) || '删除失败');
              }
            });
          }).catch(() => {});
    },
    /* ---------- 应用 ---------- */
    openApply(t) {
      this.applyTheme = t;
      this.applyFormId = null;
      this.applyVisible = true;
      if (!this.formOptions.length) this.loadForms();
    },
    doApply() {
      if (!this.applyFormId) return;
      formThemeApply(this.applyTheme.id, this.applyFormId).then(val => {
        // 后端即使失败也用 200 返回，真实结果在 data.ok 里
        const d = (val && val.data) || {};
        if (val && val.code === 200 && d.ok) {
          this.$message.success(d.message || '已套用');
          this.applyVisible = false;
        } else {
          this.$message.warning(d.message || (val && val.message) || '套用失败');
        }
      });
    },
    back() {
      const p = this.$router.push({ path: '/formList' });
      if (p && typeof p.catch === 'function') {
        p.catch(err => this.$message.error(`返回失败：${(err && err.message) || '未知错误'}`));
      }
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped>

/* @form-tokens:start */
/* ======================= 表单模块设计令牌（v3.11 视觉统一）=======================
 * ★ 本块在 9 个表单页各存一份（CSS 变量按作用域隔离，无法跨 scoped 共享）。
 *   改配色的唯一入口 = D:/tmp/shot/form_tokens.py 的 TOKEN_BLOCK，改完重跑该脚本
 *   一键同步；**不要手改单页**，否则 9 份会漂移。
 * ★ 为什么不放全局 :root：--el-color-primary 一旦全局生效会波及后台所有模块
 *   （订单 / 教练 / 财务…）。本模块只对「表单 9 页」负责，故挂在页面根容器上，
 *   作用域精确、零外溢。
 * ========================================================================== */
.ft-page {
  /* —— Element 主色对齐「焱火绿」——
     此前后台从未覆盖 --el-color-primary，Element 一直是默认**蓝色**，
     而页面自定义元素用 #8dc63f ⇒ 同屏「按钮蓝 + 品牌绿」两个颜色。
     这 7 个变量一改，本页所有 el-button / el-switch / el-radio / el-checkbox /
     el-slider / el-pagination / focus 描边… 一次性全部变绿。
     light-3/5/7/8/9 与 dark-2 必须一并给出，否则 hover / disabled 态会
     回退到 Element 基于**蓝色**算出的浅色，出现「绿底 + 蓝边」的跳变。 */
  --el-color-primary: #8dc63f;
  --el-color-primary-light-3: #afd779;
  --el-color-primary-light-5: #c6e39f;
  --el-color-primary-light-7: #ddeec5;
  --el-color-primary-light-8: #e8f4d9;
  --el-color-primary-light-9: #f4f9ec;
  --el-color-primary-dark-2: #719e32;

  /* —— 文字五档（严格对齐 Element 灰阶；置成令牌后同类文字不会再各写一个灰） —— */
  --yh-t1: #303133; /* 主文字 */
  --yh-t2: #606266; /* 常规 */
  --yh-t3: #909399; /* 次要 */
  --yh-t4: #a8abb2; /* 占位 */
  --yh-t5: #c0c4cc; /* 禁用 */

  /* —— 主色四档 —— */
  --yh-theme: #8dc63f;
  --yh-theme-d: #7cad37;
  --yh-theme-dd: #5f9427;
  --yh-theme-l: #eef7e2;
  --yh-theme-ll: #f6fbef;

  /* —— 面与线 —— */
  --yh-bg: #f5f7fa;
  --yh-fill: #fafbfc;
  --yh-line: #ebeef5;
  --yh-line-d: #dcdfe6;
  --yh-divide: #f2f3f5;

  /* —— 圆角五档 —— */
  --yh-r-xs: 4px;
  --yh-r-sm: 6px;
  --yh-r: 8px;
  --yh-r-lg: 12px;
  --yh-r-pill: 999px;
}
/* @form-tokens:end */
.ft-page {
  padding: 16px 20px 30px;
  min-height: 100%;
  background-color: #f5f7fa;
  box-sizing: border-box;
}

.ft-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 14px 20px 16px;
  margin-bottom: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.ft-title {
  display: flex;
  align-items: center;
  gap: 6px;
}

.ft-title h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 600;
  color: var(--yh-t1);
}

.ft-badge {
  padding: 1px 9px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
  font-size: 12px;
}

.ft-sub {
  margin: 6px 0 0;
  max-width: 760px;
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t3);
}

.ft-sub b {
  color: var(--yh-t2);
}

.ft-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  background: #fff;
  border-radius: var(--yh-r-lg);
  padding: 12px 16px;
  margin-bottom: 12px;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
}

.ft-bar-label {
  font-size: 13px;
  color: var(--yh-t2);
  flex: 0 0 auto;
}

.ft-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.ft-chip {
  padding: 3px 12px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-bg);
  color: var(--yh-t2);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.15s;
}

.ft-chip:hover {
  background: var(--yh-theme-l);
  color: var(--yh-theme-dd);
}

.ft-chip.is-active {
  background: var(--yh-theme);
  color: #fff;
}

/* ---------- 主题卡片 ---------- */
.ft-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(258px, 1fr));
  gap: 14px;
  min-height: 140px;
}

.ft-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: var(--yh-r-lg);
  overflow: hidden;
  box-shadow: 0 1px 4px rgba(0, 0, 21, 0.06);
  transition: all 0.18s;
}

.ft-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 0, 21, 0.1);
}

.ft-preview {
  height: 138px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-bottom: 1px solid var(--yh-divide);
}

.ft-pv-card {
  width: 100%;
  max-width: 172px;
  padding: 10px;
  background: #fff;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.07);
}

.ft-pv-title {
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
}

.ft-pv-input {
  height: 13px;
  margin-bottom: 6px;
  border-radius: var(--yh-r-xs);
  background: #f2f4f6;
}

.ft-pv-input--short {
  width: 64%;
}

.ft-pv-btn {
  height: 19px;
  margin-top: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #fff;
  letter-spacing: 1px;
}

.ft-card-body {
  flex: 1 1 auto;
  padding: 10px 12px 4px;
}

.ft-card-name {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--yh-t1);
}

.ft-preset {
  flex: 0 0 auto;
}

.ft-card-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
  font-size: 12px;
  color: var(--yh-t3);
}

.ft-style {
  padding: 1px 7px;
  border-radius: var(--yh-r-pill);
  background: var(--yh-bg);
  color: var(--yh-t2);
}

.ft-swatch {
  width: 12px;
  height: 12px;
  border-radius: var(--yh-r-xs);
  border: 1px solid rgba(0, 0, 0, 0.07);
}

.ft-hex {
  font-family: Consolas, Monaco, monospace;
  font-size: 11px;
  color: var(--yh-t4);
}

.ft-card-foot {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px 8px 8px;
  border-top: 1px solid #f2f4f6;
}

.ft-del {
  color: #f56c6c;
}

.ft-del.is-disabled {
  color: var(--yh-t5);
}

.ft-empty {
  grid-column: 1 / -1;
}

/* ---------- 编辑弹窗 ---------- */
.ft-editor {
  display: flex;
  gap: 20px;
}

.ft-editor-l {
  flex: 1 1 0;
  min-width: 0;
}

.ft-editor-r {
  flex: 0 0 232px;
  padding-left: 20px;
  border-left: 1px solid #f0f1f3;
}

.ft-editor-rlabel {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 10px;
  font-size: 12px;
  color: var(--yh-t3);
}

.ft-editor-phone {
  padding: 16px 14px;
  border-radius: var(--yh-r-pill);
  border: 1px solid var(--yh-line);
}

.ft-editor-hint {
  margin-top: 12px;
  font-size: 11px;
  line-height: 1.9;
  color: #b0b3b8;
}

.ft-editor-hint code {
  display: inline-block;
  padding: 0 3px;
  border-radius: var(--yh-r-xs);
  background: var(--yh-bg);
  font-family: Consolas, Monaco, monospace;
}

.ft-slider {
  width: 190px;
  margin-left: 2px;
}

.ft-tip {
  margin-left: 8px;
  font-size: 12px;
  color: var(--yh-t4);
}

.ft-full {
  width: 100%;
}

.ft-apply-tip {
  margin-bottom: 14px;
  padding: 10px 12px;
  border-radius: var(--yh-r);
  background: var(--yh-theme-ll);
  font-size: 12px;
  line-height: 1.8;
  color: var(--yh-t2);
}

.ft-opt-title {
  margin-right: 8px;
}

.ft-opt-code {
  font-family: Consolas, Monaco, monospace;
  font-size: 11px;
  color: var(--yh-t5);
}

@media (max-width: 900px) {
  .ft-editor {
    flex-direction: column;
  }

  .ft-editor-r {
    flex: 1 1 auto;
    padding-left: 0;
    border-left: none;
  }
}
</style>
